import { ApolloError } from '@apollo/client';
import { formatGraphQlError, validationErrors } from '@client/module/error/error';
import PrimaryButton from '@packages/ui/button/primary-button';
import SecondaryButton from '@packages/ui/button/secondary-button';
import Card from '@packages/ui/card/card';
import CardContent from '@packages/ui/card/card-content';
import CardFooter from '@packages/ui/card/card-footer';
import CardHeader from '@packages/ui/card/card-header';
import FormItem from '@packages/ui/form/form-item';
import FormLayout from '@packages/ui/form/form-layout';
import TextInput from '@packages/ui/form/input/text-input';
import Loading from '@packages/ui/loading';
import { useNotificationCenter } from '@packages/ui/notification/notification-center';
import { captureException } from '@sentry/browser';
import { useTranslate } from '@tolgee/react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useUpdateEpsonServerDirectPrint } from '../logic/use-update-epson-server-direct-print';

interface ForwardData {
  forwardTo: string;
}

export default function EpsonForwardUpdateModal(props: {
  data: { id: string; name: string; forwardTo: string | null };
  onClose: () => void;
}) {
  const [update, { loading }] = useUpdateEpsonServerDirectPrint();
  const { showAlert } = useNotificationCenter();
  const { t } = useTranslate();

  const {
    reset,
    control,
    setError,
    watch,
    formState: { errors },
  } = useForm<ForwardData>({
    defaultValues: {
      forwardTo: '',
    },
  });

  useEffect(() => {
    reset({
      forwardTo: props.data.forwardTo ?? '',
    });
  }, [props.data.forwardTo, reset]);

  if (loading) {
    return <Loading />;
  }

  const handleSubmit = async () => {
    try {
      const result = await update({
        variables: {
          id: props.data.id,
          // Blank clears it on the server, turning forwarding back off.
          input: {
            forwardTo: watch('forwardTo').trim(),
          },
        },
      });

      if (result.data) {
        showAlert({
          status: 'success',
          title: t('common.alert.success', 'Success'),
          message: result.data.updateEpsonServerDirectPrint.forwardTo
            ? t('printer.forward.alert.enabled', 'Forwarding enabled. Takes effect within 60 seconds.')
            : t('printer.forward.alert.disabled', 'Forwarding disabled. Takes effect within 60 seconds.'),
        });
        props.onClose();
      }
    } catch (err) {
      if (err instanceof ApolloError) {
        const applicationErrors = formatGraphQlError(err.graphQLErrors);
        const validationError = validationErrors(applicationErrors);
        for (const field in validationError) {
          setError(
            field as keyof ForwardData,
            {
              type: 'server',
              message: t(validationError[field] ?? 'validation-error.common.alert'),
            },
            { shouldFocus: true }
          );
        }
      }
      captureException(err);
    }
  };

  return (
    <Card>
      <CardHeader title={t('printer.forward.header', 'Forward to new print-service')} />
      <CardContent>
        <FormLayout>
          <FormItem title={t('printer.forward.title', 'Forward URL')}>
            <div>
              <div className="max-w-xl text-gray-500 italic mb-4 text-sm">
                {t(
                  'printer.forward.description',
                  'Base URL of the new print-service for {name}. While set, this printer prints jobs from BOTH systems. Leave empty to turn forwarding off.',
                  { name: props.data.name }
                )}
              </div>
              <Controller
                rules={{
                  pattern: {
                    value: /^https?:\/\/[\w-]+(\.[\w-]+)*(:\d+)?(\/\S*)?$/,
                    message: t(
                      'printer.forward.validation.url',
                      'Enter a base URL starting with http:// or https://, without the /epson path'
                    ),
                  },
                }}
                name={'forwardTo'}
                control={control}
                render={({ field }) => (
                  <TextInput
                    data-testid="forward-to-input"
                    type="text"
                    label="forwardTo"
                    value={field.value}
                    placeholder="https://print.example.com"
                    error={errors.forwardTo?.message}
                    onChange={field.onChange}
                    name=""
                  />
                )}
              />
            </div>
          </FormItem>
        </FormLayout>
      </CardContent>

      <CardFooter>
        <div className="flex w-full justify-between items-center">
          <SecondaryButton onClick={props.onClose}>{t('common.button.cancel')}</SecondaryButton>
          <PrimaryButton
            onClick={() => {
              void handleSubmit();
            }}
          >
            {t('common.button.submit')}
          </PrimaryButton>
        </div>
      </CardFooter>
    </Card>
  );
}
